/**
 * paramExtractor.ts — Parses LLM response text for a fenced JSON update block.
 *
 * Extracts and applies partial updates to SimulationParams.
 * Nested keys (e.g. "inflation.mean") are supported via dot notation.
 */

import type { SimulationParams, UserProfile, SimulationEvent, SimulationEventType } from '../engine/types'
import { newEventId } from '../engine/events'

const VALID_EVENT_TYPES: readonly SimulationEventType[] = [
  'promotion', 'job-loss', 'flat-bonus', 'medical-emergency', 'disability',
  'market-shock', 'major-expense', 'windfall', 'childcare', 'college-tuition',
  'home-purchase', 'divorce', 'long-term-care', 'sabbatical',
]

function isValidEventType(t: unknown): t is SimulationEventType {
  return typeof t === 'string' && (VALID_EVENT_TYPES as string[]).includes(t)
}

function sanitizeEvent(raw: Omit<SimulationEvent, 'id'>): Omit<SimulationEvent, 'id'> | null {
  if (!isValidEventType(raw.type)) return null
  return {
    type: raw.type,
    label: typeof raw.label === 'string' ? raw.label.slice(0, 100) : raw.type,
    startYearOffset: typeof raw.startYearOffset === 'number' && raw.startYearOffset >= 0
      ? Math.floor(raw.startYearOffset)
      : 0,
    params: typeof raw.params === 'object' && raw.params !== null ? raw.params : {},
  }
}

export interface LLMUpdate {
  scenario?: string
  update?: {
    profile?: Partial<UserProfile>
    params?: Record<string, unknown>
    addEvent?: Omit<SimulationEvent, 'id'>
    addEvents?: Array<Omit<SimulationEvent, 'id'>>  // bulk add for lifetime plans
    removeEventLabels?: string[]
    clearEvents?: boolean
  }
}

/** Extract the first ```json block from the LLM response. */
export function extractUpdate(text: string): LLMUpdate | null {
  const match = text.match(/```json\s*([\s\S]*?)```/)
  if (!match) return null
  try {
    return JSON.parse(match[1]) as LLMUpdate
  } catch {
    return null
  }
}

/** Strip the ```json block from text for display. */
export function stripJsonBlock(text: string): string {
  return text.replace(/```json[\s\S]*?```/g, '').trim()
}

/** Deep-set a dotted key path on an object (non-mutating). */
function setPath(obj: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> {
  const parts = path.split('.')
  if (parts.length === 1) return { ...obj, [path]: value }
  const [head, ...rest] = parts
  return {
    ...obj,
    [head]: setPath((obj[head] ?? {}) as Record<string, unknown>, rest.join('.'), value),
  }
}

/** Apply an LLMUpdate to current SimulationParams, returning a new params object. */
export function applyUpdate(params: SimulationParams, update: LLMUpdate): SimulationParams {
  let result: SimulationParams = { ...params }

  if (update.update?.profile) {
    result = {
      ...result,
      profile: { ...result.profile, ...update.update.profile },
    }
  }

  if (update.update?.params) {
    let patched = result as unknown as Record<string, unknown>
    for (const [key, value] of Object.entries(update.update.params)) {
      patched = setPath(patched, key, value)
    }
    result = patched as unknown as SimulationParams
  }

  if (update.update?.clearEvents) {
    result = { ...result, events: [] }
  }

  if (update.update?.addEvent) {
    const safe = sanitizeEvent(update.update.addEvent)
    if (safe) {
      const ev: SimulationEvent = { id: newEventId(), ...safe }
      result = { ...result, events: [...(result.events ?? []), ev] }
    }
  }

  if (update.update?.addEvents?.length) {
    const newEvs: SimulationEvent[] = update.update.addEvents
      .map(e => { const s = sanitizeEvent(e); return s ? { id: newEventId(), ...s } : null })
      .filter((e): e is SimulationEvent => e !== null)
    result = { ...result, events: [...(result.events ?? []), ...newEvs] }
  }

  if (update.update?.removeEventLabels?.length) {
    const labels = new Set(update.update.removeEventLabels.map(l => l.toLowerCase()))
    result = {
      ...result,
      events: (result.events ?? []).filter(e => !labels.has(e.label.toLowerCase())),
    }
  }

  return result
}
